import { concat, of, EMPTY, merge, from } from 'rxjs';
import { catchError, mergeMap, map, switchMap, debounceTime } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import get from 'lodash.get';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import intersection from 'lodash.intersection';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import Global from '../../store/global-selectors';
import { OrdersGridViewService } from '../../services/orders-grid-view-service';
import { ModalService } from 'ui-components/services/modal-service';
import { ErrorModal } from '../../components/modals/error-modal';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';
import { GenericErrorRetryContainer } from '../../containers/generic-error-retry-container';
import { mapOrdersFiltersToRequestBody } from '../../services/filtering/api-param-map-service';
import { ColumnOrderByParamService } from '../../services/column-orderby-param-service';
import { getOrderStatusFromViewName, ALL_SEARCH_RESULTS_ORDER_STATUS } from '../../services/sales-order-service';
import * as NavActions from '../../store/orders/nav/orders.nav.actions';
import * as BulkPurchaseActions from '../../store/bulk/purchase/bulk.purchase.actions';
import * as BulkUpdateActions from '../../store/bulk/update/bulk.update.actions';
import * as ODActions from '../../store/orders/order-details/orders.details.actions';
import * as PrintActions from '../../store/printing/print.actions';
import * as TagsActions from '../../store/tags/tags.actions';
import * as OrderGridActions from '../../store/orders/grid/orders.grid.actions';
import * as OrderGridHeaderActions from '../../store/orders/grid/orders.grid.header.actions';
import * as SelectedOrdersActions from '../../store/orders/selected-orders/orders.selected-orders.actions';
import { BulkErrorsAndWarningsService } from '../../services/bulk-errors-and-warnings-service';
import { OrderMarkAsPaidModalContainer } from '../../containers/mark-as-paid-modal-container/order.mark-as-paid-modal-container';
import { FulfillmentPlanAssignmentModalContainer } from '../../containers/assignment-modal-container/fulfillmentPlan.assignment-modal-container';
import * as DocumentActions from '../../store/documents/documents.actions';
import { bulkLoadFulfillmentPlans$ } from '../../store/bulkload/bulkload.observables';
import * as OrdersFilteringActions from '../../store/orders/filtering/orders.filtering.actions';
import { FilterViewProps } from '../../store/orders/filtering/filterview.properties';
import { EXTERNAL, SHIPMENT } from '../../constants/fulfillment-types';
import { DocumentType } from '../../enums/document-type';
import { UserSettings } from '../../enums/user-settings/user-settings';
import { DocumentService } from '../../services/document-service';
import * as OrdersHelpers from './orders.helpers';
import { GuidService } from 'ui-components/services/guid-service';
import * as CswActions from '../../store/csw/csw.actions';
import { DateService } from '../../services/date-service';
import * as Actions from './orders.actions';
import * as OrdersApi from './orders.api';
import { updateFulfillmentPlansHoldUntil } from '../../fulfillment-plans/fulfillment-plans.api';
import { updateFulfillmentPlanEntitiesOptimistically } from '../../store/entities/entities.actions';
import { DHLTermsAndConditionsService } from '../../services/dhl-terms-and-conditions-service';
import { promptShipstationDHLTermsAndConditions } from '../../../src/store/seller/seller.actions';
import { getIsBatchDetailsPage } from '../../store/batches/batches.helpers';
import { DEFAULT_ORDERS_PAGE_URL } from '../../services/user-permission-service';
import { hasQuickSearchTerm } from '../../services/quicksearch-service';
import { bulkErrorsAndWarningsModalObservable } from '../../store/bulk-errors-and-warnings/bulk-errors-and-warnings.observables';
import { messages } from './messages';
import { LogService } from '../../services/log-service';
import { OrderScanToSearchModal } from '../../components/modals/order-scan-to-search-modal';
import { shouldShowPrepayDutyAlert } from '../../fulfillment-planner/duties-and-taxes/helpers';
import { getBulkDutiesCount$ } from '../bulk/update/bulk.update.job.observables';
import { saveUserSetting } from '../user/user.actions';
import { BASE_CUSTOMERS_PATH } from '../../routes/constants';
import { getReduxValuesForOrderDataRequest } from './get-order-data-epic-helper';
import { ADVANCED_SEARCH_ID } from '../../pages/orders-page/orders-page-view-constants';
import { getSelectedViewFromPathName } from '../../pages/orders-page/orders-left-nav/orders-left-nav-context';
import { downloadDocument } from '../documents/documents.helpers';
import { createPackingSlipsAsync } from '../printing/async-printing/packing-slips';
import { getAmazonShipments } from '../../services/carrier-pickup-helper';
import { RateShopperPickupPrompt } from '../../carrier-pickup/carrier-pickup-modal-v2/schedule-pickup-prompt/rate-shopper-amazon-pickup';

const processOrderResponse = (
  activeFilterViewId,
  checkRequestId,
  shouldGetCounts,
  leftNavCount,
  { currentPageFulfillmentPlanIds, page, pageSize, totalCount },
) => {
  if (!checkRequestId()) {
    return EMPTY;
  }
  const numToDisplay = shouldGetCounts ? totalCount : leftNavCount;
  const updateFilterViewCount = OrdersFilteringActions.changeFilterViewProp(
    activeFilterViewId,
    FilterViewProps.FILTERVIEW_FILTER_COUNT,
    numToDisplay,
  );
  const updateCustomViewCount = OrdersFilteringActions.changeCustomFilterViewProp(FilterViewProps.FILTERVIEW_FILTER_COUNT, numToDisplay);
  const updateCountAction = activeFilterViewId === 'custom' ? updateCustomViewCount : updateFilterViewCount;

  return of(
    Actions.getOrdersSuccess(currentPageFulfillmentPlanIds, page, pageSize, numToDisplay),
    OrderGridActions.setIsLoading(false),
    updateCountAction,
  );
};

const getSelectedOrdersFulfillmentPlansAndFulfillments = (state) => {
  const fulfillmentPlanIdsInDrawer = Global.selectSelectedOrdersIds(state);
  const salesOrders = Global.selectEntitiesSalesOrdersByFulfillmentPlanIds(state, { fulfillmentPlanIds: fulfillmentPlanIdsInDrawer });
  const stateOrders = salesOrders.reduce((acc, so) => {
    if (so.fulfillmentPlanIds.length) {
      const fulfillmentPlansList = Global.selectEntitiesFulfillmentPlansBySalesOrderIds(state, {
        salesOrderIds: [so.salesOrderId],
      });
      if (so.fulfillmentIds.length) {
        const fulfillmentsList = Global.selectEntitiesFulfillmentsByIds(state, {
          fulfillmentIds: [so.fulfillmentIds],
        });
        acc.push({
          salesOrder: so,
          fulfillmentPlans: fulfillmentPlansList,
          fulfillments: fulfillmentsList,
        });
        return acc;
      }
      acc.push({ salesOrder: so, fulfillmentPlans: fulfillmentPlansList });
      return acc;
    }
    acc.push({ salesOrder: so, fulfillmentPlans: [] });
    return acc;
  }, []);
  return stateOrders;
};

const getUpdatedListOfFpIds = (selectedFulfillmentPlans, oldFPList) => {
  const selectedIds = selectedFulfillmentPlans.map((fp) => fp.fulfillmentPlanId);
  return intersection(oldFPList, selectedIds);
};

export const getOrderDataEpic = (action$, { getState }, { history }) => {
  let lastRequestGuid = GuidService.newGuid();

  return action$.pipe(
    ofType(Actions.GET_ORDERS_REQUEST, Actions.GET_SILENT_ORDERS_REQUEST),
    debounceTime(500),
    switchMap(() => {
      const state = getState();
      const { activeFilterViewId, appliedFilters, statusViewId, storeGuid, shouldGetCounts } = getReduxValuesForOrderDataRequest(
        getState(),
        history,
      );

      if (getIsBatchDetailsPage() || statusViewId === ADVANCED_SEARCH_ID) {
        return EMPTY;
      }
      const searchData = Global.selectOrdersNavSearchData(state);
      const quickSearchPerformed = hasQuickSearchTerm(searchData);
      if (quickSearchPerformed) {
        return of(NavActions.changeViewSearch(undefined, false));
      }

      const isValidView = OrdersGridViewService.isValidViewId(statusViewId);

      const isCustomersPage = history.location.pathname.includes(BASE_CUSTOMERS_PATH);

      if (isCustomersPage) {
        return EMPTY;
      }

      if (OrdersGridViewService.viewShouldRefreshAlertsGrid(statusViewId)) {
        return EMPTY;
      }

      const isOrdersPage = OrdersHelpers.getIsOrdersPage(history);
      if (!isOrdersPage) {
        LogService.logInfo('Attempted to get orders while not on the orders page.', {
          isOrdersPage,
          statusViewId,
          storeGuid,
        });
        return of(Actions.getOrdersSuccess([]));
      }

      if (!isValidView && !storeGuid) {
        return EMPTY;
      }

      const statePage = Global.selectOrdersGridPage(state);
      const statePageSize = Global.selectOrdersGridPageSize(state);
      const sellerTimeZone = Global.selectSellerTimeZoneIANA(state);

      const filterRequestJson = mapOrdersFiltersToRequestBody(appliedFilters, sellerTimeZone);
      const orderByColumn = Global.selectOrdersGridCurrentOrderByColumn(state, {
        statusViewId,
        activeFilterViewId,
      });
      const selectedGroupByEnums = Global.selectOrdersGridActiveGroupByEnums(state, { statusViewId, activeFilterViewId });

      const myRequestGuid = GuidService.newGuid();
      lastRequestGuid = myRequestGuid;

      const checkRequestId = () => myRequestGuid === lastRequestGuid;
      const requestStatusViewId = storeGuid ? statusViewId.substring(0, statusViewId.indexOf(storeGuid) - 1) : statusViewId;

      const orderByParam = ColumnOrderByParamService.getOrdersGridOrderByApiParameterOrDefault(orderByColumn, isOrdersPage);
      const groupByParams = ColumnOrderByParamService.getOrdersGridOrderByParamsFromGroupByEnums(selectedGroupByEnums);
      const allOrderBys = [...groupByParams, orderByParam];
      const dedupedOrderByParams = {};
      allOrderBys.forEach((param) => (dedupedOrderByParams[param.orderBy] = param));
      const fullRequest = {
        filter: {
          ...filterRequestJson,
        },
        orderBys: Object.values(dedupedOrderByParams),
      };

      const dedupedAndFilteredGroupByParams = ColumnOrderByParamService.getFilteredGroupByParams(Object.values(dedupedOrderByParams));

      if (storeGuid) {
        fullRequest.filter.storeGuid = [storeGuid];
      }

      const groupByCounts$ =
        groupByParams.length === 0
          ? EMPTY
          : OrdersApi.getGroupByCounts(requestStatusViewId, fullRequest.filter, dedupedAndFilteredGroupByParams).pipe(
              mergeMap((countsArr) => of(OrderGridActions.orderGridSetGroupBysCount(countsArr))),
              catchError((error) => of(Actions.getOrdersFailure(error))),
            );

      const getOrders$ = OrdersApi.getOrdersInShipmentsMode(
        requestStatusViewId,
        statePage,
        statePageSize,
        fullRequest,
        shouldGetCounts,
      ).pipe(
        mergeMap(({ currentPageFulfillmentPlanIds, page, pageSize, totalCount }) => {
          // have to recalculate here because this is after an async operation and redux may have changed
          const {
            activeFilterViewId: currentActiveFilterViewId,
            leftNavCount: currentLeftNavCount,
            shouldGetCounts: currentShouldGetCounts,
          } = getReduxValuesForOrderDataRequest(getState(), history);

          return processOrderResponse(currentActiveFilterViewId, checkRequestId, currentShouldGetCounts, currentLeftNavCount, {
            currentPageFulfillmentPlanIds,
            page,
            pageSize,
            totalCount,
          });
        }),
        catchError((error) => (checkRequestId() ? of(Actions.getOrdersFailure(error)) : EMPTY)),
      );

      return merge(getOrders$, groupByCounts$);
    }),
  );
};

const getPathname = (history) => {
  return history.location.pathname.length > 1 ? history.location.pathname : DEFAULT_ORDERS_PAGE_URL; // this fallback exists only for tests at this point. We should never get here in production. SPD-14016
};

// SPD-14016
export const changeOrdersPage = (action$, { getState }, { push, history }) => {
  return action$.pipe(
    ofType(OrderGridActions.ORDER_GRID_CHANGE_PAGE),
    mergeMap(() => {
      const state = getState();
      const userSettingPageSize = Global.selectUserUserSetting(state, {
        userSetting: UserSettings.ordersGridPageSize.settingName,
      });
      const statePage = Global.selectOrdersGridPage(state);
      const statePageSize = Global.selectOrdersGridPageSize(state);
      const pathname = getPathname(history);
      const query = new URLSearchParams(history.location.search);
      query.set('page', statePage);
      query.set('pageSize', statePageSize);
      const search = query.toString();

      const updatePageSize$ =
        userSettingPageSize !== statePageSize
          ? of(
              saveUserSetting({
                settingName: UserSettings.ordersGridPageSize.settingName,
                settingValue: statePageSize,
                excludeV3prefix: true,
              }),
            )
          : EMPTY;
      push({ pathname, search });
      return concat(of(Actions.requestGetOrders()), updatePageSize$);
    }),
  );
};

export const sanitizeOrder = (order, userDateFormat, sellerTimeZone) => {
  const sanitizedOrder = { ...order };
  const items = get(sanitizedOrder, 'items') || emptyArray;
  const amountSummary = get(sanitizedOrder, 'amountSummary') || emptyObject;
  items.forEach((item) => {
    if (!item.unitPrice.value) {
      // oxlint-disable-next-line no-param-reassign
      item.unitPrice.value = 0;
    }
  });
  Object.keys(amountSummary).forEach((key) => {
    if (!amountSummary[key].value) {
      amountSummary[key].value = 0;
    }
  });
  return {
    ...sanitizedOrder,
    orderDateTime: DateService.getDateWithTimeZoneBackendRespects(get(order, 'orderDateTime'), userDateFormat, sellerTimeZone),
    paidDateTime: DateService.getDateWithTimeZoneBackendRespects(get(order, 'paidDateTime'), userDateFormat, sellerTimeZone),
    shipByDateTime: DateService.getDateInFormatBackendRespects(get(order, 'shipByDateTime'), userDateFormat),
    holdUntilDateTime: DateService.getDateInFormatBackendRespects(get(order, 'holdUntilDateTime'), userDateFormat),
  };
};

export const createOrderEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.CREATE_ORDER_REQUEST),
    mergeMap((action) => {
      const state = getState();
      const sellerTimeZone = Global.selectSellerTimeZoneIANA(state);
      const { order, redirect, workflowGuid } = action;
      if (!order) {
        return EMPTY;
      }

      const userDateFormat = Global.selectUserUserDateFormat(state);
      const sanitizedOrder = sanitizeOrder(order, userDateFormat, sellerTimeZone);

      return OrdersApi.createOrder(sanitizedOrder).pipe(
        map(({ data }) => {
          return Actions.createOrderSuccess(data, redirect, workflowGuid);
        }),
        catchError(({ response, message }) => {
          ModalService.launchModal({
            component: GenericErrorRetryContainer,
            props: {
              action,
              title: <FormattedMessage {...messages.createOrderErrorTitle} />,
              message: <FormattedMessage {...messages.createOrderErrorMessage} />,
            },
          });

          return of(Actions.createOrderFailure(response, message, workflowGuid));
        }),
      );
    }),
  );
};

export const markAsPaidEpic = (action$) => {
  return action$.pipe(
    ofType(ODActions.MARK_PAID),
    mergeMap(
      ({
        salesOrderId,
        internalNote,
        paidDateUtc,
        orderTotal,
        shippingAmountPaid,
        orderTotalCurrencyCode,
        shippingAmountPaidCurrencyCode,
      }) => {
        const paymentData = [
          {
            salesOrderId,
            paidDateUtc,
            totalPaid: {
              value: orderTotal,
              code: orderTotalCurrencyCode,
            },
            shippingPaid: {
              value: shippingAmountPaid,
              code: shippingAmountPaidCurrencyCode,
            },
            internalNote,
          },
        ];

        return OrdersApi.markOrderAsPaid(paymentData).pipe(
          mergeMap(() => {
            const successInfo = {
              title: <FormattedMessage {...messages.setMarkPaidTitle} />,
              content: <FormattedMessage {...messages.setMarkPaidSuccessNotification} />,
              notificationType: NOTIFICATION_TYPE_SUCCESS,
              duration: 8000,
            };
            NotificationService.showNotification({ ...successInfo });
            return of(Actions.markPaidSuccess(salesOrderId));
          }),
          catchError(() => {
            const errorInfo = {
              title: <FormattedMessage {...messages.setMarkPaidTitle} />,
              content: <FormattedMessage {...messages.setMarkPaidErrorNotification} />,
              notificationType: NOTIFICATION_TYPE_ERROR,
              duration: 5000,
            };
            NotificationService.showNotification({ ...errorInfo });
            // TODO: handle error case
            return EMPTY;
          }),
        );
      },
    ),
  );
};

export const addTagsToFulfillmentPlans = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.ADD_TAGS_TO_FULFILLMENT_PLANS),
    mergeMap((action) => {
      const { fulfillmentPlanIds, tagIds } = action;
      const fulfillmentPlans =
        Global.selectEntitiesFulfillmentPlansByIds(getState(), {
          fulfillmentPlanIds,
        }) || emptyArray;
      const newFulfillmentPlans =
        fulfillmentPlans.map((fp) => {
          return {
            ...fp,
            tagIds: [...new Set([...tagIds, ...(fp?.tagIds || [])])],
          };
        }) || emptyArray;

      return of(
        updateFulfillmentPlanEntitiesOptimistically(fulfillmentPlanIds, newFulfillmentPlans),
        TagsActions.bulkAddFulfillmentPlanTags(fulfillmentPlanIds, tagIds, (retryAction) => {
          ModalService.launchModal({
            component: GenericErrorRetryContainer,
            props: {
              action: retryAction,
              title: <FormattedMessage {...messages.addTagsToOrderErrorTitle} />,
              message: <FormattedMessage {...messages.addTagsToOrderErrorMessage} />,
            },
          });
        }),
      );
    }),
  );
};

export const removeTagsFromFulfillmentPlans = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.REMOVE_TAGS_FROM_FULFILLMENT_PLANS),
    mergeMap((action) => {
      const { fulfillmentPlanIds, tagIds } = action;
      const fulfillmentPlans =
        Global.selectEntitiesFulfillmentPlansByIds(getState(), {
          fulfillmentPlanIds,
        }) || emptyArray;
      const newFulfillmentPlans = fulfillmentPlans.map((fp) => {
        return {
          ...fp,
          tagIds: fp?.tagIds?.filter((fpTagId) => !tagIds.includes(fpTagId)),
        };
      });

      return of(
        updateFulfillmentPlanEntitiesOptimistically(fulfillmentPlanIds, newFulfillmentPlans),
        TagsActions.bulkRemoveFulfillmentPlanTags(fulfillmentPlanIds, tagIds, (retryAction) => {
          ModalService.launchModal({
            component: GenericErrorRetryContainer,
            props: {
              action: retryAction,
              title: <FormattedMessage {...messages.addTagsToOrderErrorTitle} />,
              message: <FormattedMessage {...messages.addTagsToOrderErrorMessage} />,
            },
          });
        }),
      );
    }),
  );
};

export const quickSearchForOrders = (action$, { getState }, { history }) => {
  return action$.pipe(
    ofType(NavActions.ORDERS_CHANGE_VIEW_SEARCH),
    debounceTime(300),
    mergeMap(() => {
      const state = getState();
      const stores = Global.selectSellerActiveStores(state);
      const statusView = getSelectedViewFromPathName(history?.location?.pathname, stores);
      const currentViewId = statusView?.id;
      const storeGuid = statusView?.storeGuid;
      const statusId = storeGuid ? currentViewId.substring(0, currentViewId.indexOf(storeGuid) - 1) : currentViewId;
      const orderGridStatus = getOrderStatusFromViewName(statusId);

      const activeFilterView = Global.selectOrdersFilteringActiveFilterView(state);
      const activeFilterViewId = activeFilterView.filterViewId;
      const orderByColumn = Global.selectOrdersGridCurrentOrderByColumn(state, {
        statusViewId: currentViewId,
        activeFilterViewId,
      });
      const isOrdersPage = OrdersHelpers.getIsOrdersPage();
      const orderByParam = ColumnOrderByParamService.getOrdersGridOrderByApiParameterOrDefault(orderByColumn, isOrdersPage);

      const searchData = {
        ...Global.selectOrdersNavSearchData(state),
        page: Global.selectOrdersGridPage(state),
        pageSize: Global.selectOrdersGridPageSize(state),
        orderBy: orderByParam.orderBy,
        orderByDirection: orderByParam.orderByDirection,
      };
      if (orderGridStatus !== ALL_SEARCH_RESULTS_ORDER_STATUS) {
        searchData.orderGridStatus = orderGridStatus;
      }
      if (storeGuid) {
        searchData.storeGuids = storeGuid;
      }
      return OrdersApi.getOrdersFromQuickSearchInShipmentMode(searchData).pipe(
        mergeMap(({ currentPageFulfillmentPlanIds, page, pageSize, totalCount }) => {
          // const stream$ = processOrderResponse(activeFilterViewId, checkRequestId)(data);
          const results = {
            page,
            orders: currentPageFulfillmentPlanIds,
            pageSize,
            totalCount,
          };

          return concat(
            // stream$,
            of(Actions.quickSearchOrdersSuccess({ results })),
          );
        }),
        catchError((err) => of(Actions.getOrdersFailure(err), OrderGridActions.setIsLoading(false))),
      );
    }),
  );
};

export const ordersSearchTermCount = (action$, { getState }) =>
  action$.pipe(
    ofType(NavActions.ORDERS_GET_SEARCH_COUNTS),
    debounceTime(300),
    mergeMap(() => {
      const state = getState();

      const searchData = {
        ...Global.selectOrdersNavSearchData(state),
      };

      return OrdersApi.getSearchTermCountShipmentMode(searchData).pipe(
        map((data) => {
          const counts = get(data, 'statusListByStore', []);
          return NavActions.ordersGetCountsSuccess(counts);
        }),
        catchError(() => {
          ModalService.launchModal({
            component: ErrorModal,
            props: {
              title: <FormattedMessage {...messages.countsErrorTitle} />,
              message: <FormattedMessage {...messages.countsErrorMessage} />,
              state: 'warning',
            },
          });
          return EMPTY;
        }),
      );
    }),
  );

export const restoreOrder = (action$) =>
  action$.pipe(
    ofType(Actions.RESTORE_ORDER_REQUEST),
    mergeMap((action) => {
      const { salesOrderId } = action;
      const salesArray = {
        salesOrderIds: Array.isArray(salesOrderId) ? [...salesOrderId] : [salesOrderId],
      };
      return OrdersApi.restoreOrders(salesArray).pipe(
        map(() => Actions.restoreOrderSuccess(salesArray.salesOrderIds)),
        catchError(() => EMPTY),
      );
    }),
  );

export const restoreFulfillments = (action$) =>
  action$.pipe(
    ofType(Actions.RESTORE_FULFILLMENTS_REQUEST),
    mergeMap((action) => {
      const { fulfillmentPlanId } = action;
      const fpArray = {
        fulfillmentPlanIds: Array.isArray(fulfillmentPlanId) ? [...fulfillmentPlanId] : [fulfillmentPlanId],
      };
      return OrdersApi.restoreFulfillments(fpArray).pipe(
        map(() => Actions.restoreFulfillmentsSuccess(fpArray.fulfillmentPlanIds)),
        catchError(() => EMPTY),
      );
    }),
  );

export const clearGridSelections = (action$) => {
  return action$.pipe(
    ofType(Actions.RESTORE_FULFILLMENTS_SUCCESS, Actions.MARK_PAID_SUCCESS),
    mergeMap(() => {
      return of(SelectedOrdersActions.setSelectedOrdersPreCheck([]));
    }),
  );
};

export const refreshGrid = (action$, { getState }) => {
  return action$.pipe(
    ofType(
      PrintActions.MARK_LABEL_PRINTED_SUCCESS,
      Actions.SET_HOLD_UNTIL_DATE_SUCCESS,
      ODActions.MARK_AS_MODIFIED,
      Actions.RESTORE_ORDER_SUCCESS,
      Actions.ASSIGN_BULK_FULFILLMENT_PLAN_FROM_ORDERS_GRID_SUCCESS,
      Actions.CANCEL_BULK_ORDER_SUCCESS,
      BulkUpdateActions.BULK_UPDATE_JOB_COMPLETE,
      Actions.OPTIMISTIC_ASSIGN_FULFILLMENT_PLAN_FROM_ORDERS_GRID_SUCCESS,
    ),
    mergeMap(({ data }) => {
      const state = getState();
      const selectedOrdersIds = Global.selectSelectedOrdersIds(state) || [];
      const areAllSelected = Global.selectSelectedOrdersAreAllOrdersSelected(state) || false;
      const isClearSelection = selectedOrdersIds?.length === 0 && !areAllSelected;
      const shouldRefresh = !isClearSelection || data?.forceRefresh;

      if (!shouldRefresh) {
        return EMPTY;
      }
      return of(Actions.silentRequestGetOrders());
    }),
  );
};

export const handleBulkRateCompletion = (action$, { getState }) => {
  return action$.pipe(
    ofType(OrderGridHeaderActions.ORDER_GRID_RATES_UPDATED),
    mergeMap(({ data }) => {
      const state = getState();
      const selectedOrdersIds = Global.selectSelectedOrdersIds(state) || [];
      const areAllSelected = Global.selectSelectedOrdersAreAllOrdersSelected(state) || false;
      const isClearSelection = selectedOrdersIds?.length === 0 && !areAllSelected;
      const shouldRefresh = !isClearSelection || data?.forceRefresh;

      if (!shouldRefresh) {
        return EMPTY;
      }

      const allRatedIds = data?.allRatedFulfillmentPlanIds;

      if (allRatedIds?.length > 0) {
        return bulkLoadFulfillmentPlans$(allRatedIds);
      }

      return of(Actions.silentRequestGetOrders());
    }),
  );
};

export const refreshGridWithoutSelection = (action$) => {
  return action$.pipe(
    ofType(Actions.CREATE_ORDER_SUCCESS, Actions.RESTORE_FULFILLMENTS_SUCCESS),
    mergeMap(() => {
      return of(Actions.silentRequestGetOrders());
    }),
  );
};

export const setHoldUntilDate = (action$) => {
  return action$.pipe(
    ofType(Actions.SET_HOLD_UNTIL_DATE_REQUEST),
    mergeMap((action) => {
      const { payload, meta } = action;
      const { fulfillmentPlanIds, holdUntilDateTime: holdUntil } = payload;
      return from(updateFulfillmentPlansHoldUntil(fulfillmentPlanIds, holdUntil)).pipe(
        map(meta.onSuccess),
        catchError((error) => {
          ModalService.launchModal({
            component: GenericErrorRetryContainer,
            props: {
              action,
              title: <FormattedMessage {...messages.setHoldUntilErrorTitle} />,
              message: <FormattedMessage {...messages.setHoldUntilErrorMessage} />,
            },
          });
          return of(meta.onError(error));
        }),
      );
    }),
  );
};

export const purchaseBulkOrders = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.ORDERS_PURCHASE_BULK_LABELS),
    mergeMap((action) => {
      const { andPrint, fulfillmentPlanIds: selectedFulfillmentPlanIds, skipAmazonShipmentCheck } = action;
      const doNotPrint = false;
      const ignoreWarnings = false;
      const state = getState();
      const showOrderDetailsOnError = !Global.selectOrdersGridisSidebarVisible(state);
      const fulfillmentPlanIds = selectedFulfillmentPlanIds?.length ? selectedFulfillmentPlanIds : Global.selectSelectedOrdersIds(state);
      const fulfillmentPlans = fulfillmentPlanIds.map((fulfillmentPlanId) =>
        Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }),
      );
      const shouldShowShipstationTerms = DHLTermsAndConditionsService.sellerHasDhlWalletPurchaseAndNotAcceptedTerms(
        state,
        fulfillmentPlans,
      );

      const amazonShipments = getAmazonShipments(state, fulfillmentPlans);
      if (amazonShipments.length > 0 && !skipAmazonShipmentCheck) {
        ModalService.launchModal({
          component: RateShopperPickupPrompt,
          props: {
            andPrint,
            sellerLabelProviderId: amazonShipments[0]?.sellerLabelProviderId,
            createLabelAction: () => Actions.purchaseBulkLabels(andPrint, fulfillmentPlanIds, true),
          },
        });
        return EMPTY;
      }

      if (shouldShowShipstationTerms) {
        return of(promptShipstationDHLTermsAndConditions(action));
      }
      const [firstFulfillmentPlan] = fulfillmentPlans;
      if (fulfillmentPlanIds.length === 1 && firstFulfillmentPlan.fulfillmentType === SHIPMENT) {
        return of(CswActions.createLabel(fulfillmentPlanIds[0], andPrint, doNotPrint, ignoreWarnings, showOrderDetailsOnError));
      }
      if (fulfillmentPlanIds.length === 1 && firstFulfillmentPlan.fulfillmentType === EXTERNAL) {
        return of(CswActions.sendExternalFulfillment(fulfillmentPlanIds[0]));
      }

      const startBulkPurchase$ = of(BulkPurchaseActions.startBulkPurchase(fulfillmentPlanIds, andPrint));

      if (shouldShowPrepayDutyAlert(state)) {
        return getBulkDutiesCount$({
          actionOf$: startBulkPurchase$,
          fulfillmentPlanIds,
          postAcceptanceAction: Actions.purchaseBulkLabels(andPrint),
          state,
        });
      }

      return startBulkPurchase$;
    }),
  );
};

export const downloadBulkOrderLabels = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.ORDERS_BULK_DOWNLOAD_LABEL),
    mergeMap(() => {
      const state = getState();
      const stateOrders = getSelectedOrdersFulfillmentPlansAndFulfillments(state);
      const currentUserId = Global.selectUserCurrentUserId(state);
      const hasShipmentsViewRestricted = Global.selectUseruserHasShipmentViewRestriction(state);
      const ordersGridIds = Global.selectOrdersGridOrders(state);
      const labelWarningData = BulkErrorsAndWarningsService.getLabelWarningDataFromListOfOrders(
        ordersGridIds,
        stateOrders,
        currentUserId,
        hasShipmentsViewRestricted,
      );
      const {
        fulfillmentPlanIds,
        someFulfillmentsHaveLabelsPrinted,
        fulfillmentIds,
        fulfillmentPlans,
        hasFulfillmentPlansBelongingToOthers,
      } = labelWarningData;

      if (fulfillmentPlanIds.length === 0) {
        return EMPTY;
      }

      if (someFulfillmentsHaveLabelsPrinted || (hasFulfillmentPlansBelongingToOthers && hasShipmentsViewRestricted)) {
        const errorsAndWarnings = [BulkErrorsAndWarningsService.getLabelWarnings(labelWarningData)];

        const onContinueAction = () => {
          return DocumentActions.downloadDocument(DocumentType.Label, fulfillmentIds);
        };
        const errorModal$ = bulkErrorsAndWarningsModalObservable({
          continueCanBeDisabled: true,
          dataType: 'fulfillmentPlan',
          errorsAndWarnings,
          fulfillmentPlanSetId: undefined,
          hasAdditionalAction: false,
          hasSelectedItems: !!fulfillmentPlans.length,
          isBatchPrinting: false,
          onContinueAction,
          selectedItems: fulfillmentPlans,
          title: BulkErrorsAndWarningsService.getPrintWarningsTitle(),
        });

        return errorModal$;
      }

      return of(DocumentActions.downloadDocument(DocumentType.Label, fulfillmentIds));
    }),
  );
};

export const directPrintBulkOrderLabels = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.ORDERS_BULK_DIRECT_PRINT_LABEL),
    mergeMap(() => {
      const state = getState();
      const stateOrders = getSelectedOrdersFulfillmentPlansAndFulfillments(state);
      const currentUserId = Global.selectUserCurrentUserId(state);
      const hasShipmentsViewRestricted = Global.selectUseruserHasShipmentViewRestriction(state);
      const ordersGridIds = Global.selectOrdersGridOrders(state);
      const labelWarningData = BulkErrorsAndWarningsService.getLabelWarningDataFromListOfOrders(
        ordersGridIds,
        stateOrders,
        currentUserId,
        hasShipmentsViewRestricted,
      );
      const {
        fulfillmentPlanIds,
        someFulfillmentsHaveLabelsPrinted,
        fulfillmentIds,
        fulfillmentPlans,
        hasFulfillmentPlansBelongingToOthers,
      } = labelWarningData;

      if (fulfillmentPlanIds.length === 0) {
        return EMPTY;
      }

      if (someFulfillmentsHaveLabelsPrinted || (hasFulfillmentPlansBelongingToOthers && hasShipmentsViewRestricted)) {
        const errorsAndWarnings = [BulkErrorsAndWarningsService.getLabelWarnings(labelWarningData)];

        const onContinueAction = () => {
          return DocumentActions.printDocument(
            DocumentType.Label,
            fulfillmentIds,
            DocumentService.getDefaultPrintSettingForDocumentType(state, DocumentType.Label, false),
          );
        };
        const errorModal$ = bulkErrorsAndWarningsModalObservable({
          continueCanBeDisabled: true,
          dataType: 'fulfillmentPlan',
          errorsAndWarnings,
          fulfillmentPlanSetId: undefined,
          hasAdditionalAction: false,
          hasSelectedItems: !!fulfillmentPlans.length,
          isBatchPrinting: false,
          onContinueAction,
          selectedItems: fulfillmentPlans,
          title: BulkErrorsAndWarningsService.getPrintWarningsTitle(),
        });

        return errorModal$;
      }

      return of(
        DocumentActions.printDocument(
          DocumentType.Label,
          fulfillmentIds,
          DocumentService.getDefaultPrintSettingForDocumentType(state, DocumentType.Label, false),
        ),
      );
    }),
  );
};

const getPackingslipDataFromListOfOrders = (state) => {
  const nonuniqueFulfillmentPlanIds = Global.selectSelectedOrdersIds(state) || emptyArray;
  const fulfillmentPlans =
    Global.selectEntitiesFulfillmentPlansByIds(state, {
      fulfillmentPlanIds: nonuniqueFulfillmentPlanIds,
    }) || emptyArray;
  const ordersGridIds = Global.selectOrdersGridOrders(state);
  const currentUserId = Global.selectUserCurrentUserId(state);
  const fulfillmentPlanIds = intersection(ordersGridIds, nonuniqueFulfillmentPlanIds);
  const someFulfillmentPlansHavePrintedPackingSlip = fulfillmentPlans.some((fulfillmentPlan) =>
    get(fulfillmentPlan, 'packingSlipPrintedStatus.isPrinted'),
  );
  const fulfillmentPlansAssignedToOtherUsers =
    fulfillmentPlans.filter((plan) => !!get(plan, 'assignedToUserId') && get(plan, 'assignedToUserId') !== currentUserId) || emptyArray;
  const someFulfillmentPlansAreAssignedToOtherUsers = fulfillmentPlansAssignedToOtherUsers.length > 0;
  return {
    fulfillmentPlanIds,
    someFulfillmentPlansHavePrintedPackingSlip,
    someFulfillmentPlansAreAssignedToOtherUsers,
  };
};

export const downloadBulkPackingSlip = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.ORDERS_BULK_DOWNLOAD_PACKING_SLIPS),
    mergeMap(() => {
      const state = getState();
      const orders = getSelectedOrdersFulfillmentPlansAndFulfillments(state);
      const currentUserId = Global.selectUserCurrentUserId(state);
      const { fulfillmentPlanIds, someFulfillmentPlansHavePrintedPackingSlip, someFulfillmentPlansAreAssignedToOtherUsers } =
        getPackingslipDataFromListOfOrders(state);
      const shouldShowReprintWarning = Global.selectPrintSettingsShouldShowPackingSlipRePrintWarning(state);

      if (fulfillmentPlanIds.length === 0) {
        return EMPTY;
      }

      if (
        (someFulfillmentPlansHavePrintedPackingSlip && shouldShowReprintWarning) ||
        (someFulfillmentPlansAreAssignedToOtherUsers && Global.selectUseruserHasShipmentViewRestriction(state))
      ) {
        const fulfillmentPlans = [...new Set(orders.reduce((sum, order) => [...sum, ...get(order, 'fulfillmentPlans', emptyArray)], []))];
        const errorsAndWarnings = [BulkErrorsAndWarningsService.getPackingSlipWarnings(orders, currentUserId)];

        const onContinueAction = (updatedData) => {
          const updatedFulfillmentPlanIds = getUpdatedListOfFpIds(updatedData.selectedItems, fulfillmentPlanIds);
          return { updatedFulfillmentPlanIds };
        };
        const errorModal$ = bulkErrorsAndWarningsModalObservable({
          continueCanBeDisabled: true,
          dataType: 'fulfillmentPlan',
          errorsAndWarnings,
          fulfillmentPlanSetId: undefined,
          hasAdditionalAction: false,
          hasSelectedItems: !!fulfillmentPlans.length,
          isBatchPrinting: false,
          onContinueAction,
          selectedItems: fulfillmentPlans,
          title: BulkErrorsAndWarningsService.getPrintWarningsTitle(),
        });

        return errorModal$.pipe(
          mergeMap((next) => {
            if (!next.updatedFulfillmentPlanIds) {
              return of(next);
            }
            return createPackingSlipsAsync({
              fulfillmentPlanIds: next.updatedFulfillmentPlanIds,
              getState,
            }).pipe(
              mergeMap((printJob) => {
                const actions = downloadDocument(printJob.printUrl, DocumentType.PackingSlip, printJob.ids);
                return of(...actions);
              }),
            );
          }),
        );
      }

      return createPackingSlipsAsync({
        fulfillmentPlanIds,
        getState,
      }).pipe(
        mergeMap((printJob) => {
          const actions = downloadDocument(printJob.printUrl, DocumentType.PackingSlip, printJob.ids);
          return of(...actions);
        }),
      );
    }),
  );
};

export const directPrintBulkPackingSlip = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.ORDERS_BULK_DIRECT_PRINT_PACKING_SLIPS),
    mergeMap(() => {
      const state = getState();
      const orders = getSelectedOrdersFulfillmentPlansAndFulfillments(state);
      const currentUserId = Global.selectUserCurrentUserId(state);

      const { fulfillmentPlanIds, someFulfillmentPlansHavePrintedPackingSlip, someFulfillmentPlansAreAssignedToOtherUsers } =
        getPackingslipDataFromListOfOrders(state);
      const shouldShowReprintWarning = Global.selectPrintSettingsShouldShowPackingSlipRePrintWarning(state);

      if (fulfillmentPlanIds.length === 0) {
        return EMPTY;
      }

      if (
        (someFulfillmentPlansHavePrintedPackingSlip && shouldShowReprintWarning) ||
        (someFulfillmentPlansAreAssignedToOtherUsers && Global.selectUseruserHasShipmentViewRestriction(state))
      ) {
        const fulfillmentPlans = [...new Set(orders.reduce((sum, order) => [...sum, ...get(order, 'fulfillmentPlans', emptyArray)], []))];
        const errorsAndWarnings = [BulkErrorsAndWarningsService.getPackingSlipWarnings(orders, currentUserId)];

        const onContinueAction = (updatedData) => {
          const updatedFulfillmentPlanIds = getUpdatedListOfFpIds(updatedData.selectedItems, fulfillmentPlanIds);
          return DocumentActions.printDocument(
            DocumentType.PackingSlip,
            updatedFulfillmentPlanIds,
            DocumentService.getDefaultPrintSettingForDocumentType(state, DocumentType.PackingSlip, false),
          );
        };
        const errorModal$ = bulkErrorsAndWarningsModalObservable({
          continueCanBeDisabled: true,
          dataType: 'fulfillmentPlan',
          errorsAndWarnings,
          fulfillmentPlanSetId: undefined,
          hasAdditionalAction: false,
          hasSelectedItems: !!fulfillmentPlans.length,
          isBatchPrinting: false,
          onContinueAction,
          selectedItems: fulfillmentPlans,
          title: BulkErrorsAndWarningsService.getPrintWarningsTitle(),
          skipInventoryActions: true,
        });

        return errorModal$;
      }

      return of(
        DocumentActions.printDocument(
          DocumentType.PackingSlip,
          fulfillmentPlanIds,
          DocumentService.getDefaultPrintSettingForDocumentType(state, DocumentType.PackingSlip, false),
        ),
      );
    }),
  );
};

export const printBulkPackingSlip = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.ORDERS_BULK_PRINT_PACKING_SLIPS),
    mergeMap(({ autoPrint }) => {
      const state = getState();
      const orders = getSelectedOrdersFulfillmentPlansAndFulfillments(state);
      const ordersGridIds = Global.selectOrdersGridOrders(state);
      const currentUserId = Global.selectUserCurrentUserId(state);
      const { fulfillmentPlanIds, someFulfillmentPlansHavePrintedPackingSlip, someFulfillmentPlansAreAssignedToOtherUsers } =
        getPackingslipDataFromListOfOrders(state);
      const shouldShowReprintWarning = Global.selectPrintSettingsShouldShowPackingSlipRePrintWarning(state);

      if (fulfillmentPlanIds.length === 0) {
        return EMPTY;
      }

      if (
        (someFulfillmentPlansHavePrintedPackingSlip && shouldShowReprintWarning) ||
        (someFulfillmentPlansAreAssignedToOtherUsers && Global.selectUseruserHasShipmentViewRestriction(state))
      ) {
        // TODO: Don't generate new array reference each iteration. Instead push items to array in memory - AL
        const fulfillmentPlans = [...new Set(orders.reduce((sum, order) => [...sum, ...get(order, 'fulfillmentPlans', emptyArray)], []))];
        const errorsAndWarnings = [BulkErrorsAndWarningsService.getPackingSlipWarnings(orders, currentUserId)];

        const onContinueAction = (updatedData) => {
          const selectedFulfillmentPlansByGridOrder = getUpdatedListOfFpIds(updatedData.selectedItems, ordersGridIds);
          return PrintActions.printPackingSlip(selectedFulfillmentPlansByGridOrder, true, true, autoPrint);
        };

        const errorModal$ = bulkErrorsAndWarningsModalObservable({
          continueCanBeDisabled: true,
          dataType: 'fulfillmentPlan',
          errorsAndWarnings,
          fulfillmentPlanSetId: undefined,
          hasAdditionalAction: false,
          hasSelectedItems: !!fulfillmentPlans.length,
          isBatchPrinting: false,
          onContinueAction,
          selectedItems: fulfillmentPlans,
          title: BulkErrorsAndWarningsService.getPrintWarningsTitle(),
          skipInventoryActions: true,
        });

        return errorModal$;
      }

      return of(PrintActions.printPackingSlip(fulfillmentPlanIds, someFulfillmentPlansHavePrintedPackingSlip, true, autoPrint));
    }),
  );
};

const orderAssignmentErrorNotification = {
  title: <FormattedMessage {...messages.orderErrors} />,
  content: <FormattedMessage {...messages.orderAssignmentErrorTitle} />,
  notificationType: NOTIFICATION_TYPE_ERROR,
};

export const assignBulkFulfillmentPlanToUserFromOrdersGrid = (action$) => {
  return action$.pipe(
    ofType(Actions.ASSIGN_BULK_FULFILLMENT_PLAN_FROM_ORDERS_GRID),
    mergeMap((action) => {
      const fulfillmentPlanIds = get(action, 'fulfillmentPlanIds');
      const userId = get(action, 'userId');
      return OrdersApi.assignBulkFulfillmentPlanToUserFromOrdersGrid(fulfillmentPlanIds, userId).pipe(
        mergeMap((response) => {
          const { data } = response ?? {};
          const { unassignableFulfillmentPlans = [] } = data;
          if (unassignableFulfillmentPlans.length !== 0) {
            NotificationService.showNotification(orderAssignmentErrorNotification);
          }
          return of(Actions.assignBulkFulfillmentPlanToUserFromOrdersGridSuccess());
        }),
      );
    }),
  );
};

export const optimisticAssignFulfillmentPlanToUserFromOrdersGrid = (action$) =>
  action$.pipe(
    ofType(Actions.OPTIMISTIC_ASSIGN_FULFILLMENT_PLAN_FROM_ORDERS_GRID),
    mergeMap(({ payload, meta }) => {
      const { fulfillmentPlanId, userId } = payload;
      return OrdersApi.assignFulfillmentPlanToUserFromOrdersGrid(fulfillmentPlanId, userId).pipe(
        mergeMap((response) => {
          const { data } = response ?? {};
          const { unassignableFulfillmentPlans = [] } = data;
          if (unassignableFulfillmentPlans.length !== 0) {
            NotificationService.showNotification(orderAssignmentErrorNotification);
            return of(meta.onError());
          }
          return of(meta.onSuccess());
        }),
        catchError((error) => of(meta.onError(error))),
      );
    }),
  );

export const openFulfillmentPlanAssignmentModal = (action$) => {
  return action$.pipe(
    ofType(Actions.OPEN_FULFILLMENT_PLAN_ASSIGNMENT_MODAL),
    mergeMap((action) => {
      const fulfillmentPlan = get(action, 'fulfillmentPlan');
      ModalService.launchModal({
        component: FulfillmentPlanAssignmentModalContainer,
        props: {
          fulfillmentPlan,
        },
      });
      return EMPTY;
    }),
  );
};

export const cancelBulkOrderEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.CANCEL_BULK_ORDER_REQUEST),
    mergeMap((action) => {
      const notificationId = NotificationService.showNotification({
        title: <FormattedMessage {...messages.bulkCancelShipmentTitle} />,
        content: <FormattedMessage {...messages.bulkCancelShipmentProcessingMessage} />,
        notificationType: 'info',
        progress: true,
        dontHide: true,
      });
      const state = getState();
      const salesOrderIds = get(action, 'salesOrderIds', emptyArray);
      const actualFulfillmentPlanIds = Global.selectEntitiesFulfillmentPlanIdsBySalesOrderIds(state, {
        salesOrderIds,
      });
      const fulfillmentPlanIds = actualFulfillmentPlanIds.length > 0 ? actualFulfillmentPlanIds : salesOrderIds;
      const cancelFunction = action?.shouldVoidFulfillments
        ? OrdersApi.cancelFulfillmentPlansAndVoidFulfillments
        : OrdersApi.cancelFulfillmentPlans;

      return cancelFunction({ fulfillmentPlanIds }).pipe(
        map(() => {
          const shipmentsCancelled = fulfillmentPlanIds.length;
          const content = <FormattedMessage {...messages.bulkCancelShipmentSuccessMessage} values={{ shipmentsCancelled }} />;
          NotificationService.updateNotification(notificationId, {
            title: <FormattedMessage {...messages.bulkCancelShipmentTitle} />,
            content,
            notificationType: NOTIFICATION_TYPE_SUCCESS,
            dontHide: false,
            progress: false,
          });
          return Actions.bulkCancelOrderSuccess();
        }),
        catchError(() => {
          NotificationService.showNotification({
            title: <FormattedMessage {...messages.bulkCancelShipmentTitle} />,
            content: <FormattedMessage {...messages.bulkCancelShipmentErrorMessage} />,
            notificationType: NOTIFICATION_TYPE_ERROR,
            progress: false,
            dontHide: false,
          });
          return EMPTY;
        }),
      );
    }),
  );
};

export const bulkCancelJobComplete = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.BULK_CANCEL_JOB_COMPLETE),
    mergeMap(({ pusherJobGuid, data }) => {
      const state = getState();
      const matchingJobs = Global.selectBulkCancelBulkCancelJobsByPusherJobGuid(state, { pusherJobGuid });
      const lastAddedJobId = Global.selectBulkCancelLastAddedJobId(state);
      if (matchingJobs.length === 0) {
        return EMPTY;
      }
      const actions = [];
      matchingJobs.forEach((job) => {
        const { pusherJobGuids, id, notificationId } = job;
        const filteredPusherJobGuids = pusherJobGuids.filter((guid) => guid !== pusherJobGuid);
        const jobIsCompletelyDone = filteredPusherJobGuids.length === 0;
        if (!jobIsCompletelyDone) {
          actions.push(Actions.updateBulkCancelJob(id, filteredPusherJobGuids, notificationId));
          return;
        }
        const { Errors: ordersFailedToCancel, Total } = data;
        const shipmentsCancelled = Total - ordersFailedToCancel;
        const content = <FormattedMessage {...messages.bulkCancelShipmentSuccessMessage} values={{ shipmentsCancelled }} />;
        NotificationService.updateNotification(notificationId, {
          title: <FormattedMessage {...messages.bulkCancelShipmentTitle} />,
          content,
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          dontHide: false,
          progress: false,
        });
        if (ordersFailedToCancel > 0) {
          const failureMessageContent = (
            <FormattedMessage {...messages.bulkCancelShipmentFailureMessage} values={{ shipmentsFailedToCancel: ordersFailedToCancel }} />
          );
          NotificationService.showNotification({
            title: <FormattedMessage {...messages.bulkCancelShipmentTitle} />,
            content: failureMessageContent,
            notificationType: NOTIFICATION_TYPE_ERROR,
            dontHide: false,
            progress: false,
          });
        }
        actions.push(Actions.deleteBulkCancelJob(lastAddedJobId));
        actions.push(Actions.silentRequestGetOrders());
      });
      return concat(actions);
    }),
  );
};

export const openMarkAsPaidModal = (action$) => {
  return action$.pipe(
    ofType(Actions.OPEN_MARK_AS_PAID_MODAL),
    mergeMap(({ salesOrderId }) => {
      ModalService.launchModal({
        component: OrderMarkAsPaidModalContainer,
        props: {
          salesOrderId,
        },
      });
      return EMPTY;
    }),
  );
};

export const openScanToSearchModal = (action$) =>
  action$.pipe(
    ofType(Actions.OPEN_SCAN_TO_SEARCH_MODAL),
    mergeMap(() => {
      ModalService.launchModal({
        component: OrderScanToSearchModal,
        props: {},
      });
      return EMPTY;
    }),
  );
